import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useOrder, useUpdateOrderStatus, STATUS_LABEL, STATUS_PILL, PAYMENT_LABEL, type OrderStatus } from "@/lib/orders";
import { supabase } from "@/lib/supabase";

const fmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const dfmt = new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

const NEXT_STATUS: Partial<Record<OrderStatus, OrderStatus[]>> = {
  placed: ["planned", "cancelled"],
  planned: ["in_delivery", "cancelled"],
  in_delivery: ["delivered", "failed"],
};

export default function BestellungDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading } = useOrder(id);
  const upd = useUpdateOrderStatus();

  const productIds = order?.items?.map((i) => i.product_id) ?? [];
  const { data: products = [] } = useQuery({
    queryKey: ["product-names", productIds.join(",")],
    enabled: productIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("products").select("id,name,unit").in("id", productIds);
      return data ?? [];
    },
  });
  const nameById = new Map(products.map((p) => [p.id, p]));

  if (isLoading) return <div className="card">Lade…</div>;
  if (!order) return (
    <div className="card">
      Bestellung nicht gefunden. <Link to="/backoffice/bestellungen" className="underline text-wiese-700">Übersicht</Link>
    </div>
  );

  const nextOptions = NEXT_STATUS[order.status] ?? [];

  return (
    <div className="space-y-4">
      <div>
        <Link to="/backoffice/bestellungen" className="text-sm text-hof-600 hover:text-hof-900">← zurück</Link>
        <h1 className="text-3xl font-bold text-hof-900">Bestellung {order.order_number ?? ""}</h1>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className={STATUS_PILL[order.status]}>{STATUS_LABEL[order.status]}</span>
          <span className="text-hof-500 text-sm">{PAYMENT_LABEL[order.payment_method]} · Quelle: {order.source}</span>
        </div>
      </div>

      {nextOptions.length > 0 && (
        <div className="card flex items-center flex-wrap gap-2">
          <span className="text-hof-700 font-medium">Status ändern auf:</span>
          {nextOptions.map((s) => (
            <button key={s} onClick={() => id && upd.mutate({ id, status: s })} disabled={upd.isPending}
              className="btn-secondary">
              → {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      )}

      <section className="card grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h2 className="font-semibold text-hof-800 mb-1">Kunde</h2>
          <div className="text-hof-900">{order.customer?.display_name ?? "—"}</div>
          <div className="text-sm text-hof-600">{order.customer?.phone ?? ""}</div>
          {order.customer?.email && <div className="text-sm text-hof-600">{order.customer.email}</div>}
        </div>
        <div>
          <h2 className="font-semibold text-hof-800 mb-1">Lieferung</h2>
          <div className="text-hof-900">{dfmt.format(new Date(order.delivery_date))}</div>
          {order.address && (
            <div className="text-sm text-hof-700">{order.address.street}, {order.address.zip} {order.address.city}</div>
          )}
        </div>
      </section>

      {order.notes && (
        <section className="card">
          <h2 className="font-semibold text-hof-800 mb-1">Notiz</h2>
          <p className="text-hof-700">{order.notes}</p>
        </section>
      )}

      <section className="card">
        <h2 className="font-semibold text-hof-800 mb-2">Artikel</h2>
        <table className="w-full text-left">
          <thead className="text-sm text-hof-600">
            <tr><th>Produkt</th><th className="text-right">Menge</th><th className="text-right">Preis</th><th className="text-right">Zeile</th></tr>
          </thead>
          <tbody>
            {(order.items ?? []).map((i) => {
              const p = nameById.get(i.product_id);
              return (
                <tr key={i.id} className="border-t border-hof-100">
                  <td className="py-2">{p?.name ?? i.product_id}</td>
                  <td className="py-2 text-right font-mono">{i.qty}</td>
                  <td className="py-2 text-right font-mono">{fmt.format(i.unit_price_cents / 100)}</td>
                  <td className="py-2 text-right font-mono">{fmt.format((i.qty * i.unit_price_cents) / 100)}</td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-hof-300 font-bold">
              <td colSpan={3} className="py-2 text-right">Gesamt</td>
              <td className="py-2 text-right text-wiese-700">{fmt.format(order.total_cents / 100)}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}
